import { Component, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { auditTime } from 'rxjs';
import Highcharts from 'highcharts';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { InvestorDashboardPortfolioSeriesBalanceSummary, PortfolioHierarchy } from 'src/app/models/classes/investor-dashboard.class';
import { DeviceType } from 'src/app/models/enums/breakpoint.enum';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { CYAN, GROSVENOR_BLUE, MIST, NAVY, ROYAL_BLUE, WHITE } from '@gcm/gcm-ui-angular';
import { MathComputationService } from 'src/app/services/math-computation.service';
import { BaseWidgetComponent } from 'src/app/shared/base-widget/base-widget.component';
import { GcmChartComponent } from 'src/app/shared/gcm-chart/gcm-chart.component';

@Component({
    selector: 'app-cumulative-chart',
    imports: [BaseWidgetComponent, GcmChartComponent, TourMatMenu],
    templateUrl: './cumulative-chart.component.html',
    styleUrls: ['./cumulative-chart.component.scss']
})
export class CumulativeChartComponent {
  private breakpointService = inject(BreakpointService);
  private currencyFormat = inject(CurrencyFormatPipe);
  private mathService = inject(MathComputationService);

  cumulativeChartStyle = { width: '100%', display: 'flex' };
  cumulativeChartOptions = {};
  readonly cumulativeData = input.required<PortfolioHierarchy>();
  readonly selectedAsOfDate = input.required<string>();
  linelegendsymbol: ((legend: Highcharts.Legend, item: Highcharts.Series | Highcharts.Point) => void) | null = null;
  error = '';
  reflowEvent: unknown;
  private readonly deviceType = toSignal(
    this.breakpointService.getDeviceType().pipe(auditTime(50)),
    { initialValue: DeviceType.Web }
  );

  private readonly rebuildChart = effect(() => {
    const cumulativeData = this.cumulativeData();
    this.selectedAsOfDate();
    this.deviceType();
    if (cumulativeData) {
      this.createCumulativeChart(cumulativeData);
    }
  });

  createCumulativeChart(portfolio: PortfolioHierarchy) {
    const portfolioSereisSummaries = portfolio.portfolioBalanceSummary;

    const chartData = this.mathService.filterChartDataBeforeSpecificYear(portfolioSereisSummaries);
    chartData.forEach(element => {
      element.disNavSum = (element.distributions ?? 0) + (element.nav ?? 0);
      element.month = new Date(element.reportingPeriod).getMonth() + 1;
      element.year = new Date(element.reportingPeriod).getFullYear();
      const quarter = element.month / 3;
      element.quarter = `Q${quarter}<br/>${element.year}`;
    });
    let sortedData = chartData.sort((a, b) => new Date(a.reportingPeriod).getTime() - (new Date(b.reportingPeriod)).getTime());
    let cumulativeChartData:InvestorDashboardPortfolioSeriesBalanceSummary[];
    if (sortedData) {
      sortedData = sortedData.filter(data => new Date(data.reportingPeriod.slice(0, 10)) <= new Date(this.selectedAsOfDate()));
    }
    if (sortedData && sortedData.length >= 16) {
      const recentQuarterValues = sortedData.slice(Math.max(sortedData.length - 4, 0));
      cumulativeChartData = sortedData.filter(data => data.month === 12 && !recentQuarterValues.includes(data)).concat(recentQuarterValues);
    }
    else {
      cumulativeChartData = sortedData;
    }

    //Creating Cumulative widget with Highcharts
    type HcInternal = typeof Highcharts & { seriesTypes: Record<string, { prototype: Record<string, unknown> }> };
    const hc = Highcharts as HcInternal;
    this.linelegendsymbol = hc.seriesTypes.line.prototype['drawLegendSymbol'] as typeof this.linelegendsymbol;
    hc.seriesTypes.line.prototype['drawLegendSymbol'] = hc.seriesTypes.area.prototype['drawLegendSymbol'];
    {
      const isMobileDevice = this.deviceType() === DeviceType.Handset;
      this.cumulativeChartOptions = {
        chart: {
          height: 400
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: cumulativeChartData.map(x => x.quarter),
          labels: {
            staggerLines: isMobileDevice ? 2 : 0,
            style: {
              fontSize: isMobileDevice ? '10px' : '12px'
            }
          }
        },
        yAxis: {
          title: {
            text: ''
          },
          labels: {
            enabled: true,
            reserveSpace: true,
            formatter: (context: Highcharts.AxisLabelsFormatterContextObject) => {
              //show in millions, if less than 1 million then show 1 decimal point
              const numVal = Number(context.value);
              const fractionDigits = Math.abs(numVal) >= 1000000 ? 0 : 1
              const valToShow = this.currencyFormat.transform(numVal, portfolio.currency, 'Millions', false, false, fractionDigits, true);
              return valToShow;
            },
            style: {
              fontSize: isMobileDevice ? '10px' : '12px'
            }
          },
          gridLineDashStyle: 'Dash',
          gridLineWidth: 1,
          gridLineColor: MIST
        },
        legend: {
          enabled: true,
          align: 'left',
          verticalAlign: 'top',
          itemStyle: {
            cursor: 'default',
            fontWeight: 'light',
            fontSize: isMobileDevice ? '11px' : '13px'
          },
          itemHoverStyle: {
            transition: 'none'
          },
          symbolHeight: 12,
          symbolWidth: 12,
          x: -18,
          events: {
            itemClick: () => false
          }
        },
        tooltip: {
          backgroundColor: ROYAL_BLUE,
          borderColor: WHITE,
          borderRadius: 8,
          style: {
            color: WHITE,
            fontSize: isMobileDevice ? '11px' : '14px'
          },
          useHTML: true,
          formatter: function (this: Highcharts.Point) {
            const pipe = new CurrencyFormatPipe();
            const valToShow = pipe.transform(Number(this.y), portfolio.currency, 'Millions', false, false, 1, true);
            const data = `${this.series.name} <br/>` + valToShow + ` <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${WHITE}">`
            const footer = `${this.key}`
            return data + footer
          },
          borderWidth: 0.5,
          shadow: true
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: false
            }
          },
          column: {
            stacking: 'normal',
            maxPointWidth: 42,
            dataLabels: {
              enabled: false
            },
            states: {
              inactive: {
                opacity: 1
              }
            }
          }
        },
        series: [{
          name: 'Distributions',
          data: cumulativeChartData.map(x => x.distributions),
          type: 'column',
          color: GROSVENOR_BLUE,
          states: {
            hover: {
              color: GROSVENOR_BLUE
            }
          }
        }, {
          name: 'Net Asset Value',
          data: cumulativeChartData.map(x => x.nav),
          type: 'column',
          color: NAVY,
          states: {
            hover: {
              color: NAVY
            }
          }
        }, {
          name: 'Contributions',
          data: cumulativeChartData.map(x => x.contributions),
          type: 'line',
          color: CYAN,
          borderColor: CYAN,
          states: {
            hover: {
              lineWidth: 2,
            },
            inactive: {
              opacity: 1
            }
          },
          marker: {
            enabled: false
          }
        }]
      }
    }

  }
}
